/**
 * pUSD wrap/unwrap 交易构造库
 *
 * 用于 V2 环境下 USDC.e ↔ pUSD 互换:
 * - wrap:  USDC.e → pUSD (充值后做、setup-guide 引导用)
 * - unwrap:pUSD → USDC.e (提现时做)
 *
 * 合约:CollateralOnramp / CollateralOfframp
 * ABI:  wrap(asset, to, amount) / unwrap(asset, to, amount)
 * 来源:https://docs.polymarket.com/concepts/pusd
 */

import { encodeFunctionData, parseAbi } from 'viem';
import { type Transaction } from './predict-relayer';
import { CONTRACTS, createErc20ApproveTx } from './predict-approvals';

// V2 合约地址(已 export 自 predict-approvals.ts CONTRACTS,本文件再次声明仅为可读性)
export const COLLATERAL_ONRAMP = CONTRACTS.collateralOnramp;
export const COLLATERAL_OFFRAMP = CONTRACTS.collateralOfframp;
export const PUSD_ADDRESS = CONTRACTS.pusd;

const wrapAbi = parseAbi(['function wrap(address asset, address to, uint256 amount)']);
const unwrapAbi = parseAbi(['function unwrap(address asset, address to, uint256 amount)']);

/**
 * 构造 wrap 两笔交易(approve USDC.e + wrap),给 Relayer 批量执行
 *
 * @param amountWei - 包装数量(USDC.e 6 decimals 单位)
 * @param toAddress - 接收 pUSD 的地址(通常传 Safe 地址)
 * @returns 两笔交易:[approve(USDC.e, Onramp), wrap(USDC.e, toAddress, amount)]
 */
export function buildWrapTxs(amountWei: bigint, toAddress: string): Transaction[] {
  return [
    createErc20ApproveTx(CONTRACTS.usdc, COLLATERAL_ONRAMP, amountWei),
    {
      to: COLLATERAL_ONRAMP,
      data: encodeFunctionData({
        abi: wrapAbi,
        functionName: 'wrap',
        args: [CONTRACTS.usdc as `0x${string}`, toAddress as `0x${string}`, amountWei],
      }),
      value: '0',
    },
  ];
}

/**
 * 构造 unwrap 两笔交易(approve pUSD + unwrap)
 *
 * @param amountWei - 解包数量(pUSD 6 decimals 单位)
 * @param toAddress - 接收 USDC.e 的地址(提现场景直接填用户目标地址)
 * @returns 两笔交易:[approve(pUSD, Offramp), unwrap(USDC.e, toAddress, amount)]
 *
 * 注意:unwrap 的 asset 参数填要接收的 token (USDC.e),不是要解包的 token
 */
export function buildUnwrapTxs(amountWei: bigint, toAddress: string): Transaction[] {
  return [
    createErc20ApproveTx(CONTRACTS.pusd, COLLATERAL_OFFRAMP, amountWei),
    {
      to: COLLATERAL_OFFRAMP,
      data: encodeFunctionData({
        abi: unwrapAbi,
        functionName: 'unwrap',
        args: [CONTRACTS.usdc as `0x${string}`, toAddress as `0x${string}`, amountWei],
      }),
      value: '0',
    },
  ];
}
